import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { ConfirmDialog } from '@/components/ui';

/**
 * Unsaved-changes protection for the platform console.
 *
 * The app uses the declarative <BrowserRouter>, where react-router's `useBlocker` is not
 * available, so protection is layered:
 *  - navigation started from console chrome (sidebar, breadcrumbs, in-page links that use
 *    `guardedNavigate`) asks for confirmation while any form is dirty;
 *  - closing the tab / refreshing triggers the browser's native unsaved-changes prompt.
 * Browser back/forward buttons are not intercepted.
 */
interface DirtyGuardValue {
  setDirty: (id: string, dirty: boolean) => void;
  isDirty: () => boolean;
  guardedNavigate: (to: string) => void;
}

const Ctx = createContext<DirtyGuardValue | null>(null);

export function DirtyGuardProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const dirty = useRef(new Set<string>());
  const [pending, setPending] = useState<string | null>(null);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirty.current.size === 0) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const setDirty = useCallback((id: string, isDirty: boolean) => {
    if (isDirty) dirty.current.add(id);
    else dirty.current.delete(id);
  }, []);

  const value = useMemo<DirtyGuardValue>(
    () => ({
      setDirty,
      isDirty: () => dirty.current.size > 0,
      guardedNavigate: (to: string) => {
        if (dirty.current.size > 0) setPending(to);
        else navigate(to);
      },
    }),
    [navigate, setDirty],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      <ConfirmDialog
        open={!!pending}
        onOpenChange={(o) => !o && setPending(null)}
        title="Discard unsaved changes?"
        description="You have changes that haven’t been saved. Leaving this page will discard them."
        confirmLabel="Discard and leave"
        cancelLabel="Keep editing"
        tone="danger"
        onConfirm={() => {
          const to = pending;
          dirty.current.clear();
          setPending(null);
          if (to) navigate(to);
        }}
      />
    </Ctx.Provider>
  );
}

/** Outside the platform shell the guard is a no-op, so shared components still work. */
const NOOP: DirtyGuardValue = { setDirty: () => undefined, isDirty: () => false, guardedNavigate: () => undefined };

export function useDirtyGuard(): DirtyGuardValue {
  return useContext(Ctx) ?? NOOP;
}

/** Registers a form's dirty state with the guard for as long as the component is mounted. */
export function useRegisterDirty(isDirty: boolean) {
  const { setDirty } = useDirtyGuard();
  const id = useRef(`form-${Math.random().toString(36).slice(2)}`).current;
  useEffect(() => {
    setDirty(id, isDirty);
  }, [id, isDirty, setDirty]);
  useEffect(() => () => setDirty(id, false), [id, setDirty]);
}
